import { useState, useEffect } from 'react';
import './Forms.css';
import { useNavigate } from 'react-router-dom';
import { getProjects } from '../services/ProjectServices';
import { sendInvitationEmail } from '../services/InvitationService';
import { createSeller } from '../services/SellerService';
import { getRolesByCompanyId } from '../services/RoleServices';


function UserInvitePanel({ companyId, onClose }) {
    const navigate = useNavigate();
    const [userData, setUserData] = useState({
        email: '',
        projects: [],
        firstName: '',
        lastName: '',
        phone: '',
        role: ''
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [allProjects, setAllProjects] = useState([]);
    const [allRoles, setAllRoles] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState('');

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await getProjects();
                const data = response.data || response;
                const filtered = data.filter(project => project.companyId === companyId);
                setAllProjects(filtered);
            } catch (error) {
                console.error('Error fetching projects:', error);
            }
        };

        const fetchRoles = async () => {
            try {
                const response = await getRolesByCompanyId(companyId);
                const data = response.data || response;
                setAllRoles(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error('Error fetching roles:', error);
            }
        };

        fetchProjects();
        fetchRoles();
    }, [companyId]);

    const validateForm = () => {
        const newErrors = {};
        if (!userData.email.trim()) {
            newErrors.email = 'El correo electrónico es requerido';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
            newErrors.email = 'Ingrese un correo válido';
        }

        if (userData.projects.length === 0) {
            newErrors.projects = 'Seleccione al menos un proyecto';
        }

        if (!userData.role.trim()) {
            newErrors.role = 'El rol es requerido';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleProjectSelection = (projectId) => {
        const project = allProjects.find(p => p.id === projectId);
        setUserData(prev => {
            const alreadySelected = prev.projects.find(p => p.id === projectId);
            const updatedProjects = alreadySelected
                ? prev.projects.filter(p => p.id !== projectId)
                : [...prev.projects, { id: project.id, name: project.name, slug: project.slug }];
            return { ...prev, projects: updatedProjects };
        });
        if (errors.projects) {
            setErrors(prev => ({ ...prev, projects: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        setMessage('');

        try {
            await sendInvitationEmail({ ...userData, companyId });

            for (const project of userData.projects) {
                await createSeller({
                    projectId: project.id,
                    email: userData.email,
                    name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
                    phone: userData.phone || '',
                    role: userData.role

                });
            }

            setModalMessage('Invitación y registro de seller exitoso.');
            setShowModal(true);
            setUserData({ email: '', projects: [], firstName: '', lastName: '', phone: '' });

            setTimeout(() => navigate(-1), 2000);
        } catch (error) {
            console.error('Error al enviar invitación o crear seller:', error);
            setMessage({
                text: `Error: ${error.response?.data?.message || error.message}`,
                type: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-container">
            <div className="form-container-user">
                <h2>Invitar nuevo usuario</h2>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="firstName">Nombre*</label>
                        <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={userData.firstName || ''}
                            onChange={handleChange} />
                    </div>

                    <div className="form-group">
                        <label htmlFor="lastName">Apellido*</label>
                        <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={userData.lastName || ''}
                            onChange={handleChange} />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Correo electrónico*</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            placeholder="ejemplo@dominio.com"
                            value={userData.email}
                            onChange={handleChange}
                            className={errors.email ? 'input-error' : ''} />
                        {errors.email && <span className="error-message">{errors.email}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone">Teléfono</label>
                        <input
                            type="text"
                            id="phone"
                            name="phone"
                            value={userData.phone || ''}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="role">Cargo / Rol en la empresa*</label>
                        <select
                            id="role"
                            name="role"
                            value={userData.role || ''}
                            onChange={handleChange}
                            className={errors.role ? 'input-error' : ''}
                        >
                            <option value="">Seleccione un rol</option>
                            {allRoles.map((role) => (
                                <option key={role.id} value={role.name}>
                                    {role.name}
                                </option>
                            ))}
                        </select>
                        {errors.role && <span className="error-message">{errors.role}</span>}
                    </div>

                    <div className="form-group">
                        <label>Proyectos asignados*</label>
                        {allProjects.length === 0 ? (
                            <p className="no-projects-message">No hay proyectos disponibles</p>
                        ) : (
                            <div className="project-checkboxes-container">
                                {allProjects.map(project => (
                                    <div key={project.id} className="checkbox-item">
                                        <input
                                            type="checkbox"
                                            id={`project-${project.id}`}
                                            checked={userData.projects.some(p => p.id === project.id)}
                                            onChange={() => handleProjectSelection(project.id)}
                                            className="custom-checkbox"
                                        />
                                        <label htmlFor={`project-${project.id}`} className="checkbox-label">
                                            {project.name}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        )}
                        {errors.projects && <span className="error-message">{errors.projects}</span>}
                    </div>

                    <div className="form-actions">
                        <button type="button" onClick={onClose} className="btn-cancel" disabled={loading}>
                            Cancelar
                        </button>
                        <button type="submit" className="btn-submit" disabled={loading}>
                            {loading ? (
                                <>
                                    <i className="icon-spinner spinner"></i>
                                    {' '}Enviando...
                                </>
                            ) : 'Enviar invitación'}
                        </button>
                    </div>

                    {message && (
                        <div className={`form-message ${message.type}`}>
                            {message.text}
                        </div>
                    )}
                </form>
                {showModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <p>{modalMessage}</p>
                            <button type="button" className="btn-submit"
                                onClick={() => {
                                    setShowModal(false);
                                    navigate(-1);
                                }}>
                                Cerrar
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}

export default UserInvitePanel;
